import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { productionAPI } from '../services/api';
import { millProductionAPI } from '../services/millAPI';
import MobileScrollTable from '../components/common/MobileScrollTable';
import BatchDispositionModal, { DispositionPayload, MatchingFeedBank } from './BatchDispositionModal';
import { pickSettled } from './dispositionLoads';
import { useDraft, useDraftRestore } from '../contexts/DraftContext';
import DraftNotice from '../components/DraftNotice';
import './Production.css';

interface BatchIngredient {
  id: number;
  raw_material_id: number;
  raw_material_name: string;
  recipe_percentage: number;
  planned_quantity: number;
  actual_quantity: number;
  remaining_quantity: number;
  is_added: boolean;
  added_at: string | null;
  in_stock: number;
  stock_status: 'sufficient' | 'low' | 'out';
  scale: 'BULK' | 'MICRO';
}

interface BatchAllocation {
  id: number;
  order_number: string;
  order_id: number;
  order_item_id: number;
  customer_name: string;
  special_instructions: string | null;
  allocated_weight: number;
  order_item_total_lbs: number;
  already_filled_lbs: number;
  package_type: string;
  package_type_display: string;
  number_of_packages: number;
  is_pelleted: boolean;
  feed_form: string;
}

interface BatchMixingData {
  id: number;
  batch_number: string;
  formula_id: number;
  formula_name: string;
  feed_grade: string;
  feed_grade_display: string;
  animal_type: string;
  animal_type_display: string;
  feed_form: string;
  feed_form_display: string;
  status: string;
  status_display: string;
  planned_weight: number;
  actual_weight: number | null;
  total_added: number;
  total_remaining: number;
  started_at: string | null;
  completed_at: string | null;
  notes: string;
  ingredients: BatchIngredient[];
  allocations: BatchAllocation[];
  post_milling: {
    pellets_needed: number;
    crumbles_needed: number;
    mash_needed: number;
  };
  orders_count: number;
}

const BatchMixing: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine if we're in Mill Portal context
  const isMillPortal = location.pathname.startsWith('/mill');

  const [batch, setBatch] = useState<BatchMixingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Ingredient input states - pre-filled with NEEDED values
  const [ingredientInputs, setIngredientInputs] = useState<{ [key: number]: string }>({});

  // Draft persistence: the operator's entered quantities survive in-tab navigation.
  // ingredientInputs drives inventory consumption, so it's a SENSITIVE draft field.
  const { hasDraft, getDraft, setDraftField, setBaseline, clearDraft } = useDraft();
  const draftKey = `batch-mixing:${id || 'new'}`;
  const serverInputsRef = useRef<{ [key: number]: string }>({});
  // Commit an operator edit to both local state and the draft store (sensitive).
  const commitInputs = useCallback((next: { [key: number]: string }) => {
    setIngredientInputs(next);
    setDraftField(draftKey, 'ingredientInputs', next, { sensitive: true });
  }, [draftKey, setDraftField]);
  const draftRestore = useDraftRestore(draftKey, (batch as any)?.updated_at ?? null, {
    labels: { ingredientInputs: 'Ingredient quantities' },
    onUseServer: () => setIngredientInputs(serverInputsRef.current),
  });

  // Success message toast
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Modals
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [newBatchWeight, setNewBatchWeight] = useState('');
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  // Disposition window options (fetched on open)
  const [matchingProducts, setMatchingProducts] = useState<Array<{
    id: number; name: string; sku: string; size_lbs: number; form: string;
  }>>([]);
  const [matchingBins, setMatchingBins] = useState<Array<{
    id: number; name: string; product_id: number | null; capacity_lbs: number;
    current_lbs: number; available_capacity_lbs: number; percentage_full: number;
  }>>([]);
  const [matchingFeedBank, setMatchingFeedBank] = useState<MatchingFeedBank[]>([]);

  // Low stock warning modal
  const [showLowStockWarning, setShowLowStockWarning] = useState(false);
  const [lowStockItems, setLowStockItems] = useState<Array<{
    name: string;
    current_stock: number;
    amount_to_deduct: number;
    resulting_balance: number;
  }>>([]);

  // Use appropriate API based on portal context
  const api = isMillPortal ? millProductionAPI : productionAPI;

  const loadBatch = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError('');
      const response = isMillPortal
        ? await millProductionAPI.getMixingBatch(parseInt(id))
        : await productionAPI.getBatchMixing(parseInt(id));
      setBatch(response.data);

      // Pre-fill ingredient inputs with NEEDED values (planned_quantity)
      // If already added, use the actual_quantity
      const inputs: { [key: number]: string } = {};
      response.data.ingredients.forEach((ing: BatchIngredient) => {
        if (ing.is_added && ing.actual_quantity > 0) {
          inputs[ing.raw_material_id] = ing.actual_quantity.toString();
        } else {
          // Pre-fill with needed amount
          inputs[ing.raw_material_id] = ing.planned_quantity.toString();
        }
      });
      // Remember the server-derived prefill so "use current version" can restore it.
      serverInputsRef.current = inputs;
      // A retained draft (operator navigated away mid-mix and came back) wins over
      // the prefill; otherwise capture the server version as the draft baseline.
      if (hasDraft(draftKey)) {
        setIngredientInputs(getDraft(draftKey)!.fields.ingredientInputs || inputs);
      } else {
        setIngredientInputs(inputs);
        setBaseline(draftKey, (response.data as any)?.updated_at ?? null);
      }

      setNewBatchWeight(response.data.planned_weight.toString());
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message || 'Unknown error';
      setError(`Failed to load batch details: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [id, isMillPortal, draftKey, hasDraft, getDraft, setBaseline]);

  useEffect(() => {
    loadBatch();
  }, [loadBatch]);

  // Show success toast
  const showSuccessToast = (message: string) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 4000);
  };

  // Calculate running total from current inputs
  const runningTotal = useMemo(() => {
    return Object.values(ingredientInputs).reduce((sum, val) => {
      const num = parseFloat(val);
      return sum + (isNaN(num) ? 0 : num);
    }, 0);
  }, [ingredientInputs]);

  // Check if any input differs from planned
  const hasAdjustments = useMemo(() => {
    if (!batch) return false;
    return batch.ingredients.some((ing) => {
      const inputVal = parseFloat(ingredientInputs[ing.raw_material_id] || '0');
      return Math.abs(inputVal - ing.planned_quantity) > 0.01;
    });
  }, [batch, ingredientInputs]);

  // Add ALL ingredients at once
  const handleAddAllIngredients = async () => {
    if (!batch) return;

    // Collect all ingredients with their amounts
    const ingredientsToAdd = batch.ingredients
      .map((ing) => ({
        raw_material_id: ing.raw_material_id,
        quantity: parseFloat(ingredientInputs[ing.raw_material_id] || '0'),
      }))
      .filter((ing) => ing.quantity > 0);

    if (ingredientsToAdd.length === 0) {
      alert('Please enter quantities for at least one ingredient');
      return;
    }

    try {
      setSaving(true);

      const response = isMillPortal
        ? await millProductionAPI.addAllIngredients(batch.id, ingredientsToAdd)
        : await productionAPI.addAllIngredients(batch.id, ingredientsToAdd);

      // Update local state without full reload
      const updatedIngredients = batch.ingredients.map((ing) => {
        const addedIng = response.data.added_ingredients?.find(
          (a: any) => a.raw_material_id === ing.raw_material_id
        );
        if (addedIng) {
          return {
            ...ing,
            actual_quantity: addedIng.quantity,
            is_added: true,
            added_at: new Date().toISOString(),
          };
        }
        return ing;
      });

      setBatch({
        ...batch,
        total_added: response.data.total_added,
        total_remaining: batch.planned_weight - response.data.total_added,
        status: response.data.batch_status,
        ingredients: updatedIngredients,
      });

      clearDraft(draftKey);   // saved to the server — the draft is done
      showSuccessToast(response.data.message || 'All ingredients added successfully!');
    } catch (err: any) {
      console.error('Error adding ingredients:', err);
      alert('Failed to add ingredients: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Reset all inputs to planned values
  const handleResetInputs = () => {
    if (!batch) return;

    const inputs: { [key: number]: string } = {};
    batch.ingredients.forEach((ing) => {
      inputs[ing.raw_material_id] = ing.planned_quantity.toString();
    });
    commitInputs(inputs);
  };

  const handleClearAll = async () => {
    if (!batch) return;

    if (!window.confirm('Clear all ingredient additions? This will reset all amounts to zero.')) {
      return;
    }

    try {
      setSaving(true);
      isMillPortal
        ? await millProductionAPI.clearIngredients(batch.id)
        : await productionAPI.clearIngredients(batch.id);

      // Reset local state
      const inputs: { [key: number]: string } = {};
      const updatedIngredients = batch.ingredients.map((ing) => {
        inputs[ing.raw_material_id] = ing.planned_quantity.toString();
        return {
          ...ing,
          actual_quantity: 0,
          is_added: false,
          added_at: null,
        };
      });

      setIngredientInputs(inputs);
      clearDraft(draftKey);   // server-cleared — no stale draft to restore
      setBatch({
        ...batch,
        total_added: 0,
        total_remaining: batch.planned_weight,
        ingredients: updatedIngredients,
      });

      showSuccessToast('All ingredients cleared. Ready to start fresh.');
    } catch (err: any) {
      console.error('Error clearing ingredients:', err);
      alert('Failed to clear ingredients: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleAdjustBatchSize = async () => {
    if (!batch) return;

    const newWeight = parseFloat(newBatchWeight);
    if (isNaN(newWeight) || newWeight <= 0) {
      alert('Please enter a valid weight');
      return;
    }

    try {
      setSaving(true);
      isMillPortal
        ? await millProductionAPI.adjustBatchSize(batch.id, { new_weight: newWeight })
        : await productionAPI.adjustBatchSize(batch.id, newWeight);
      setShowAdjustModal(false);
      await loadBatch(); // Need full reload to get recalculated planned amounts
      showSuccessToast(`Batch size adjusted to ${newWeight.toLocaleString()} lbs`);
    } catch (err: any) {
      console.error('Error adjusting batch size:', err);
      alert('Failed to adjust batch size: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Holds the disposition payload from the window so a low-stock force-retry can
  // resubmit the same legs with force=true.
  const [pendingDisposition, setPendingDisposition] = useState<DispositionPayload | null>(null);

  const submitCompletion = async (payload: DispositionPayload, force = false) => {
    if (!batch) return;
    if (batch.total_added <= 0) {
      alert('No ingredients have been added. Add ingredients before completing.');
      return;
    }
    const data = { ...payload, force };
    try {
      setSaving(true);
      const response = isMillPortal
        ? await millProductionAPI.completeMixing(batch.id, data)
        : await productionAPI.completeMixing(batch.id, data);

      const result = response.data;
      let message = `Batch completed!\n\nMilled: ${result.total_added.toLocaleString()} lbs`;
      if (result.distribution_results?.length > 0) {
        message += `\n\nExcess placed:`;
        for (const dr of result.distribution_results) message += `\n  - ${dr.detail}`;
      }
      const fills = result.order_fills || [];
      const complete = fills.filter((f: any) => f.complete).length;
      const open = fills.length - complete;
      message += `\n\n${complete} order line(s) milled${open > 0 ? `, ${open} still open (multi-batch)` : ''}`;
      message += `\n${result.ingredients_deducted} ingredient(s) deducted`;

      clearDraft(draftKey);   // batch completed — the draft is done
      alert(message);
      navigate(isMillPortal ? '/mill/dashboard' : '/production');
    } catch (err: any) {
      const resp = err.response?.data;
      // 409 — negative inventory: offer force.
      if (err.response?.status === 409 && resp?.error === 'low_stock_warning') {
        setLowStockItems(resp.low_stock_items);
        setPendingDisposition(payload);
        setShowCompleteModal(false);
        setShowLowStockWarning(true);
        return;
      }
      // 400 — disposition rejected (imbalance / order_short / bagged remainder).
      // The window pre-validates these, so this is a backstop; surface the detail.
      if (err.response?.status === 400 && resp?.code) {
        let m = resp.error || 'Disposition rejected.';
        if (resp.remainders?.length) m += '\n\n' + resp.remainders.map((r: any) => r.message).join('\n');
        if (resp.shortfalls?.length) m += '\n\n' + resp.shortfalls.map((s: any) => s.message).join('\n');
        alert(m);
        return;
      }
      console.error('Error completing batch:', err);
      alert('Failed to complete batch: ' + (resp?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDispositionSubmit = (payload: DispositionPayload) => {
    setShowCompleteModal(false);
    submitCompletion(payload, false);
  };

  const handleForceComplete = async () => {
    setShowLowStockWarning(false);
    if (pendingDisposition) await submitCompletion(pendingDisposition, true);
  };

  const openCompleteModal = async () => {
    if (!batch) return;
    // Always load destination options — the disposition window shows the locked
    // order portion + excess placement + make-up sources for every completion.
    // Load the three lists INDEPENDENTLY (allSettled): a failure in one endpoint
    // must NOT blank the others, or the excess dropdown would collapse to
    // Feed-Bank-only even when bagged/bins data is fine (see doc 22).
    const dapi = isMillPortal ? millProductionAPI : productionAPI;
    const [prodRes, binRes, bankRes] = await Promise.allSettled([
      dapi.matchingStoreProducts(batch.id),
      dapi.matchingByocBins(batch.id),
      dapi.matchingFeedBank(batch.id),
    ]);
    if (prodRes.status === 'rejected') console.error('matchingStoreProducts failed:', prodRes.reason);
    if (binRes.status === 'rejected') console.error('matchingByocBins failed:', binRes.reason);
    if (bankRes.status === 'rejected') console.error('matchingFeedBank failed:', bankRes.reason);
    setMatchingProducts(pickSettled(prodRes));
    setMatchingBins(pickSettled(binRes));
    setMatchingFeedBank(pickSettled(bankRes));
    setShowCompleteModal(true);
  };

  const calculateProgress = () => {
    if (!batch) return 0;
    return batch.planned_weight > 0 ? (batch.total_added / batch.planned_weight) * 100 : 0;
  };

  // Handle keyboard navigation (Tab through inputs, Enter to submit)
  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter' && index === (batch?.ingredients.length || 0) - 1) {
      // Enter on last input submits all
      e.preventDefault();
      handleAddAllIngredients();
    }
  };

  if (loading) {
    return (
      <div className="production-page">
        <div className="production-loading">
          <div className="loading-spinner"></div>
          <p>Loading batch details...</p>
        </div>
      </div>
    );
  }

  if (error || !batch) {
    return (
      <div className="production-page">
        <div className="batch-detail-error">
          <p>{error || 'Batch not found'}</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate(isMillPortal ? '/mill/dashboard' : '/production')}
          >
            Back to {isMillPortal ? 'Mill Dashboard' : 'Production'}
          </button>
        </div>
      </div>
    );
  }

  const isCompleted = ['COMPLETED', 'PACKAGING', 'PACKAGED', 'SHIPPED'].includes(batch.status);
  const canEdit = !isCompleted;
  const allIngredientsAdded = batch.ingredients.every((ing) => ing.is_added);
  // Group by physical scale: BULK first, MICRO after; order within each is the
  // API's %-desc. (Plain const — batch is non-null past the loading/error guards.)
  const orderedIngredients = [
    ...batch.ingredients.filter((i) => i.scale === 'BULK'),
    ...batch.ingredients.filter((i) => i.scale !== 'BULK'),
  ];

  return (
    <div className="production-page batch-mixing-page">
      {/* Restore banner / stale-record choice for a retained draft */}
      <DraftNotice controller={draftRestore} />
      {/* Success Toast */}
      {showSuccess && (
        <div className="success-toast">
          <span className="success-icon">&#10003;</span>
          {successMessage}
        </div>
      )}

      {/* Header */}
      <div className="batch-mixing-header">
        <button
          className="btn btn-secondary back-btn"
          onClick={() => navigate(isMillPortal ? '/mill/dashboard' : '/production')}
        >
          &larr; Back to {isMillPortal ? 'Mill Dashboard' : 'Production'}
        </button>
        <div className="batch-mixing-title">
          <h1>
            <span className="batch-icon">&#127981;</span> {batch.batch_number}: {batch.formula_name}
          </h1>
          <div className="batch-mixing-subtitle">
            <span className="badge badge-grade">{batch.feed_grade_display}</span>
            <span className="badge badge-animal">{batch.animal_type_display}</span>
            <span className="badge badge-form">{batch.feed_form_display}</span>
            <span className={`badge badge-status status-${batch.status.toLowerCase()}`}>
              {batch.status_display}
            </span>
          </div>
        </div>
      </div>

      {/* Progress Summary Cards */}
      <div className="mixing-summary-cards">
        <div className="summary-card">
          <div className="summary-label">Target</div>
          <div className="summary-value">{batch.planned_weight.toLocaleString()} lbs</div>
        </div>
        <div className="summary-card highlight-green">
          <div className="summary-label">Added</div>
          <div className="summary-value">{batch.total_added.toLocaleString()} lbs</div>
        </div>
        <div className="summary-card highlight-yellow">
          <div className="summary-label">Remaining</div>
          <div className="summary-value">{batch.total_remaining.toLocaleString()} lbs</div>
        </div>
        {canEdit && (
          <div className={`summary-card ${runningTotal === batch.planned_weight ? 'highlight-green' : 'highlight-blue'}`}>
            <div className="summary-label">Input Total</div>
            <div className="summary-value">{runningTotal.toLocaleString()} lbs</div>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mixing-progress-container">
        <div className="mixing-progress-bar">
          <div
            className="mixing-progress-fill"
            style={{ width: `${Math.min(calculateProgress(), 100)}%` }}
          />
        </div>
        <div className="mixing-progress-label">{calculateProgress().toFixed(1)}% Complete</div>
      </div>

      {/* Action Buttons */}
      {canEdit && (
        <div className="mixing-actions">
          <button
            className="btn btn-secondary"
            onClick={() => setShowAdjustModal(true)}
            disabled={saving}
          >
            Adjust Batch Size
          </button>
          <button
            className="btn btn-warning"
            onClick={handleClearAll}
            disabled={saving || batch.total_added === 0}
          >
            Clear All
          </button>
          <button
            className="btn btn-success btn-large"
            onClick={openCompleteModal}
            disabled={saving || batch.total_added === 0}
          >
            Complete Batch
          </button>
        </div>
      )}

      {/* Recipe Ingredients Table */}
      <div className="mixing-section">
        <h2>Recipe Ingredients</h2>
        {hasAdjustments && canEdit && (
          <div className="adjustment-notice">
            <span className="notice-icon">&#9888;</span>
            Some amounts differ from recipe. Review highlighted rows before adding.
          </div>
        )}
        <div className="mixing-table-container">
          <MobileScrollTable>
          <table className="mixing-table">
            <thead>
              <tr>
                <th>Ingredient</th>
                <th>%</th>
                <th>Needed</th>
                <th>Amount to Add</th>
              </tr>
            </thead>
            <tbody>
              {/* Grouped by physical scale: BULK first, MICRO below. Order within
                  each group is the API's %-desc. Global index preserves keyboard nav. */}
              {orderedIngredients.map((ing, index) => {
                const inputValue = parseFloat(ingredientInputs[ing.raw_material_id] || '0');
                const isDifferent = Math.abs(inputValue - ing.planned_quantity) > 0.01;
                const prev = orderedIngredients[index - 1];
                const showBulkHeader = ing.scale === 'BULK' && (!prev || prev.scale !== 'BULK');
                const showMicroHeader = ing.scale !== 'BULK' && (!prev || prev.scale === 'BULK');

                return (
                  <React.Fragment key={ing.id}>
                    {showBulkHeader && (
                      <tr className="scale-group-header"><td colSpan={4}>BULK SCALE</td></tr>
                    )}
                    {showMicroHeader && (
                      <tr className="scale-group-header"><td colSpan={4}>MICRO SCALE</td></tr>
                    )}
                    <tr
                      className={`ingredient-row ${ing.is_added ? 'added' : ''} ${isDifferent && canEdit ? 'adjusted' : ''}`}
                    >
                      <td className="ingredient-name">
                        {ing.raw_material_name}
                        {ing.is_added && <span className="added-check">&#10003;</span>}
                      </td>
                      <td className="ingredient-percent">{ing.recipe_percentage.toFixed(2)}%</td>
                      <td className="ingredient-needed">{ing.planned_quantity.toLocaleString()}</td>
                      <td className="ingredient-added">
                        {canEdit ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            className={`ingredient-input ${isDifferent ? 'input-adjusted' : ''}`}
                            value={ingredientInputs[ing.raw_material_id] || ''}
                            onChange={(e) =>
                              commitInputs({
                                ...ingredientInputs,
                                [ing.raw_material_id]: e.target.value,
                              })
                            }
                            onBlur={(e) => {
                              const val = parseFloat(e.target.value);
                              if (!isNaN(val) && val >= 0) {
                                commitInputs({
                                  ...ingredientInputs,
                                  [ing.raw_material_id]: String(val),
                                });
                              } else {
                                commitInputs({
                                  ...ingredientInputs,
                                  [ing.raw_material_id]: '',
                                });
                              }
                            }}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                            disabled={saving}
                          />
                        ) : (
                          <span>{ing.actual_quantity.toLocaleString()}</span>
                        )}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="totals-row">
                <td colSpan={2}><strong>TOTAL</strong></td>
                <td><strong>{batch.planned_weight.toLocaleString()}</strong></td>
                <td><strong>{runningTotal.toLocaleString()}</strong></td>
              </tr>
            </tfoot>
          </table>
          </MobileScrollTable>
        </div>

        {/* Add All / Reset Buttons */}
        {canEdit && (
          <div className="ingredient-actions">
            <button
              className="btn btn-secondary"
              onClick={handleResetInputs}
              disabled={saving}
            >
              Reset to Recipe
            </button>
            <button
              className="btn btn-primary btn-large"
              onClick={handleAddAllIngredients}
              disabled={saving}
            >
              {saving ? 'Adding...' : allIngredientsAdded ? 'Update All Ingredients' : 'Add All Ingredients'}
            </button>
          </div>
        )}

        {/* Second Complete Batch — identical to the top one, under Update All */}
        {canEdit && (
          <div className="ingredient-actions">
            <button
              className="btn btn-success btn-large"
              onClick={openCompleteModal}
              disabled={saving || batch.total_added === 0}
            >
              Complete Batch
            </button>
          </div>
        )}
      </div>

      {/* Orders in this Batch */}
      <div className="mixing-section">
        <h2>Orders in This Batch ({batch.orders_count})</h2>
        <div className="orders-list">
          {batch.allocations.map((alloc) => (
            <div key={alloc.id} className="order-allocation-card">
              <div className="order-info">
                <span className="order-number">Order #{alloc.order_number}</span>
                <span className="customer-name">{alloc.customer_name}</span>
              </div>
              {alloc.special_instructions && (
                <div className="customer-instructions-banner">
                  <strong>📋 Customer Instructions:</strong>
                  <p>{alloc.special_instructions}</p>
                </div>
              )}
              <div className="order-details">
                <span className="allocated-weight">
                  {alloc.allocated_weight.toLocaleString()} lbs
                </span>
                <span className="package-type">
                  {alloc.package_type === 'BULK'
                    ? 'Bulk'
                    : `${alloc.number_of_packages} ${alloc.package_type_display}`}
                </span>
                {alloc.is_pelleted && <span className="badge badge-pellet">Pellet</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Post-Milling Breakdown */}
      <div className="mixing-section">
        <h2>Post-Milling Processing</h2>
        <div className="post-milling-cards">
          <div className="post-milling-card">
            <div className="post-milling-label">Pellets to Make</div>
            <div className="post-milling-value">
              {batch.post_milling.pellets_needed.toLocaleString()} lbs
            </div>
          </div>
          <div className="post-milling-card">
            <div className="post-milling-label">Mash (No Processing)</div>
            <div className="post-milling-value">
              {batch.post_milling.mash_needed.toLocaleString()} lbs
            </div>
          </div>
        </div>
      </div>

      {/* Adjust Batch Size Modal */}
      {showAdjustModal && (
        <div className="modal-overlay" onClick={() => setShowAdjustModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Adjust Batch Size</h3>
              <button className="modal-close" onClick={() => setShowAdjustModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>New Target Weight (lbs)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-input"
                  value={newBatchWeight}
                  onChange={(e) => setNewBatchWeight(e.target.value)}
                  onBlur={(e) => {
                    const val = parseFloat(e.target.value);
                    if (!isNaN(val) && val > 0) {
                      setNewBatchWeight(String(val));
                    }
                  }}
                />
              </div>
              <p className="help-text">
                Current target: {batch.planned_weight.toLocaleString()} lbs
              </p>
              <p className="help-text">
                This will recalculate all ingredient quantities based on the new batch size.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAdjustModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAdjustBatchSize}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Update Batch Size'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Whole-batch disposition window (2a-4) */}
      {showCompleteModal && batch && (
        <BatchDispositionModal
          batchNumber={batch.batch_number}
          formulaName={batch.formula_name}
          totalMilled={batch.total_added}
          allocations={batch.allocations}
          products={matchingProducts}
          bins={matchingBins}
          feedBank={matchingFeedBank}
          saving={saving}
          onCancel={() => setShowCompleteModal(false)}
          onSubmit={handleDispositionSubmit}
        />
      )}

      {/* Low Stock Warning Modal */}
      {showLowStockWarning && (
        <div className="modal-overlay" onClick={() => setShowLowStockWarning(false)}>
          <div className="modal-content low-stock-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ borderBottom: '3px solid #f59e0b' }}>
              <h3 style={{ color: '#b45309' }}>Low Stock Warning</h3>
              <button className="modal-close" onClick={() => setShowLowStockWarning(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>Completing this batch will result in <strong>negative inventory</strong> for:</p>
              <table className="low-stock-table" style={{ width: '100%', borderCollapse: 'collapse', margin: '12px 0' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                    <th style={{ padding: '8px 4px' }}>Ingredient</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right' }}>In Stock</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right' }}>To Deduct</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right' }}>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStockItems.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 4px' }}>{item.name}</td>
                      <td style={{ padding: '8px 4px', textAlign: 'right' }}>{item.current_stock.toLocaleString()} lbs</td>
                      <td style={{ padding: '8px 4px', textAlign: 'right' }}>{item.amount_to_deduct.toLocaleString()} lbs</td>
                      <td style={{ padding: '8px 4px', textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>
                        {item.resulting_balance.toLocaleString()} lbs
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ color: '#6b7280', fontSize: '0.9em' }}>
                You can still complete the batch, but inventory will show negative values until restocked.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowLowStockWarning(false)}
              >
                Cancel
              </button>
              <button
                className="btn"
                onClick={handleForceComplete}
                disabled={saving}
                style={{ backgroundColor: '#f59e0b', color: '#fff', border: 'none' }}
              >
                {saving ? 'Completing...' : 'Complete Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchMixing;
